/**
 * features/reports/HodAnalysisTab.tsx
 * ------------------------------------------------------------------
 * The HOD "Teacher-to-Teacher" department analysis — the school's main
 * analysis artifact. Department-scoped:
 *   • a HOD sees ONLY their department(s) and can generate the whole
 *     department in one document OR each subject individually,
 *   • management (admin / head — the wildcard scope) can pick any
 *     department AND generate every department they can see at once.
 * Excel + PDF, all forms, matching the school template (lib/hodAnalysis).
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { FileSpreadsheet, FileText, Building2, BookOpen, Loader2, Layers } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  curriculumDepartmentsFor, // department scope resolver (HOD → their depts; admin/head → null)
} from '@/lib/permissions';
import { allDepartments as allDepts, isDeptVisible, sameDept, departmentOfSubject } from '@/lib/departments';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { buildHodAnalysis, HOD_GRADES } from '@/lib/hodAnalysis';
import { buildHodAnalysisXlsxBlob } from '@/lib/hodAnalysisXlsx';
import { buildHodAnalysisPdf } from '@/lib/analysisPdf';
import { SoftCard, glassFieldClass } from '@/components/ui/glass';
import { TermKey, termLabel } from '@/lib/school.config';
import type { MarksDocument, Subject } from '@/types';

const tag = (s: string) => s.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export default function HodAnalysisTab({ year, term }: { year: string; term: TermKey }) {
  const { activeStudents: students, subjects, classes, teachers } = useData();
  const { userData } = useAuth();

  const myDepartments = useMemo(() => curriculumDepartmentsFor(userData, teachers), [userData, teachers]);
  const deptScope = useMemo<string[] | null>(() => (myDepartments && myDepartments.length ? myDepartments : null), [myDepartments]);
  const allDepartments = useMemo(() => allDepts(subjects, teachers), [subjects, teachers]);
  const visibleDepartments = useMemo(() => {
    if (deptScope === null) return allDepartments;
    const scoped = allDepartments.filter((d) => isDeptVisible(d, deptScope));
    return scoped.length ? scoped : deptScope;
  }, [deptScope, allDepartments]);

  const [dept, setDept] = useState('');
  useEffect(() => {
    if (visibleDepartments.length && (!dept || !visibleDepartments.some((d) => sameDept(d, dept)))) {
      setDept(visibleDepartments[0]);
    }
  }, [visibleDepartments, dept]);

  const deptSubjects = useMemo(
    () => subjects.filter((s) => sameDept(departmentOfSubject(s, teachers), dept)).sort((a, b) => a.name.localeCompare(b.name)),
    [subjects, teachers, dept],
  );

  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function loadMarks(subs: Subject[]): Promise<MarksDocument[]> {
    const ids = subs.map((s) => s.id);
    const docs: MarksDocument[] = [];
    for (let i = 0; i < ids.length; i += 10) {
      const chunk = ids.slice(i, i + 10);
      const snap = await getDocs(query(collection(db, COLLECTIONS.marks), where('year', '==', year), where('term', '==', term), where('subjectId', 'in', chunk)));
      snap.docs.forEach((d) => docs.push(d.data() as MarksDocument));
    }
    return docs;
  }

  function download(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }

  async function generate(subs: Subject[], scopeLabel: string, fileTag: string, format: 'xlsx' | 'pdf', busyKey: string) {
    if (subs.length === 0) return;
    setBusy(busyKey); setMsg(null);
    try {
      const docs = await loadMarks(subs);
      const classNameById: Record<string, string> = {};
      classes.forEach((c) => { classNameById[c.id] = c.name; });
      const blocks = buildHodAnalysis({ term, deptSubjects: subs, teachers, students, markDocs: docs, classNameById });
      if (blocks.length === 0) { setMsg({ ok: false, text: `No marks found for ${scopeLabel} this term.` }); return; }
      const titleLine = `${scopeLabel} — ${termLabel(term)} ${year}`;
      const base = `HOD_Analysis_${fileTag}_${term}_${year}`;
      if (format === 'xlsx') {
        download(await buildHodAnalysisXlsxBlob(blocks, titleLine), `${base}.xlsx`);
      } else {
        const logo = await loadLogoDataUrl();
        buildHodAnalysisPdf({
          title: 'DEPARTMENT ANALYSIS',
          metaLine: `${scopeLabel}  ·  ${termLabel(term)} ${year}`,
          grades: HOD_GRADES,
          subjects: blocks.map((b) => ({ subjectName: b.subjectName, forms: b.forms.map((f) => ({ formLabel: f.formLabel, rows: f.rows })) })),
          logoDataUrl: logo,
          generatedBy: userData?.displayName,
        }).save(`${base}.pdf`);
      }
      setMsg({ ok: true, text: `Generated ${scopeLabel} (${format.toUpperCase()}).` });
    } catch (e) {
      console.error(e);
      setMsg({ ok: false, text: `Could not generate — ${(e as Error).message || 'please retry.'}` });
    } finally {
      setBusy(null);
    }
  }

  const allVisibleSubjects = useMemo(
    () => subjects.filter((s) => visibleDepartments.some((d) => sameDept(departmentOfSubject(s, teachers), d))),
    [subjects, teachers, visibleDepartments],
  );

  const Btn = ({ onClick, busyKey, icon: Icon, children }: { onClick: () => void; busyKey: string; icon: typeof FileText; children: ReactNode }) => (
    <button
      onClick={onClick}
      disabled={!!busy}
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
    >
      {busy === busyKey ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />} {children}
    </button>
  );

  return (
    <div className="space-y-4">
      {msg && (
        <div className={`text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300'}`}>{msg.text}</div>
      )}

      {/* Department picker (scoped) */}
      <SoftCard pad="p-4">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Department</label>
            {visibleDepartments.length > 1 ? (
              <select value={dept} onChange={(e) => setDept(e.target.value)} className={glassFieldClass('sm:w-64')}>
                {visibleDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            ) : (
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 py-2">{dept || '—'}</div>
            )}
          </div>
          <p className="text-xs text-gray-400 sm:pb-2">
            {deptScope === null
              ? 'Management view — pick any department, or generate them all.'
              : 'You can generate reports for your department only.'}
          </p>
        </div>
      </SoftCard>

      {/* Whole department */}
      <SoftCard pad="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-primary" />
            <div>
              <div className="font-semibold text-sm text-gray-800 dark:text-gray-100">Whole department — {dept || '—'}</div>
              <div className="text-xs text-gray-400">{deptSubjects.length} subject{deptSubjects.length === 1 ? '' : 's'} · all forms · one document</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Btn busyKey="dept:xlsx" icon={FileSpreadsheet} onClick={() => generate(deptSubjects, `${dept} Department`, tag(dept), 'xlsx', 'dept:xlsx')}>Excel</Btn>
            <Btn busyKey="dept:pdf" icon={FileText} onClick={() => generate(deptSubjects, `${dept} Department`, tag(dept), 'pdf', 'dept:pdf')}>PDF</Btn>
          </div>
        </div>
      </SoftCard>

      {/* Per subject */}
      <SoftCard pad="p-0">
        <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <BookOpen size={14} className="text-gray-500" />
          <h3 className="font-semibold text-sm text-gray-800 dark:text-white">Individual subjects</h3>
        </div>
        {deptSubjects.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 p-4">No subjects resolve to this department.</p>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {deptSubjects.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <span className="text-sm text-gray-800 dark:text-gray-100">{s.name}</span>
                <div className="flex gap-2">
                  <Btn busyKey={`subj:${s.id}:xlsx`} icon={FileSpreadsheet} onClick={() => generate([s], s.name, tag(`${dept}_${s.name}`), 'xlsx', `subj:${s.id}:xlsx`)}>Excel</Btn>
                  <Btn busyKey={`subj:${s.id}:pdf`} icon={FileText} onClick={() => generate([s], s.name, tag(`${dept}_${s.name}`), 'pdf', `subj:${s.id}:pdf`)}>PDF</Btn>
                </div>
              </div>
            ))}
          </div>
        )}
      </SoftCard>

      {/* All departments — management (or a multi-department HOD) */}
      {visibleDepartments.length > 1 && (
        <SoftCard pad="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Layers size={16} className="text-indigo-500" />
              <div>
                <div className="font-semibold text-sm text-gray-800 dark:text-gray-100">All departments</div>
                <div className="text-xs text-gray-400">{visibleDepartments.length} departments · every subject · one combined document</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Btn busyKey="all:xlsx" icon={FileSpreadsheet} onClick={() => generate(allVisibleSubjects, 'All Departments', 'All_Departments', 'xlsx', 'all:xlsx')}>Excel</Btn>
              <Btn busyKey="all:pdf" icon={FileText} onClick={() => generate(allVisibleSubjects, 'All Departments', 'All_Departments', 'pdf', 'all:pdf')}>PDF</Btn>
            </div>
          </div>
        </SoftCard>
      )}
    </div>
  );
}
